// ============================================
// Projects list — the ONE status filter + customer grouping (Dispatch AND AR/AP)
// ============================================
// Side-effect module (UMD): attaches `window.ProjectStatusFilter`. app.js imports
// it once, before any view module; projects.js and billing.js read it through
// `window.ProjectStatusFilter` at CALL time — never a cross-`?v=` ES import
// (CLAUDE.md Pitfall #1). Node unit tests require() it directly.
//
// WHY. Lacey (TITS bookkeeper, 2026-10-07): "the projects folder in AR/AP doesn't
// match the projects folder in Dispatch — they should match." Both read the same
// tenants/{t}/projects collection, but Dispatch defaulted to Active with an exact
// `status ===` test (a project with no status showed only under All) while AR/AP
// defaulted to "Money owed" and treated a missing status as active. Same data,
// two answers. Everything that decides WHICH projects a status chip shows, what
// the chips are called, their order and the default lives here so the two lists
// cannot drift again.
//
// STATUS. Lower-cased + trimmed; a missing/blank status reads as 'active' (what
// createProject writes, and what AR/AP already assumed). A status outside the
// three known ones (hand-edited data) shows under All only — in BOTH views.
//
// GROUPING. Key = customer id when there is one, else the lower-cased
// denormalized name, else "No customer". Label = the project's own
// primaryCustomerName (what the Dispatch card prints), falling back to the
// customer doc's name. Groups sort A–Z, "No customer" last.
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && typeof root === 'object') root.ProjectStatusFilter = api;
})(typeof window !== 'undefined' ? window : null, function () {
  const DEFAULT = 'active';
  const KNOWN = ['active', 'completed', 'archived'];
  // Order is the order both views render the chips in.
  const CHIPS = Object.freeze([
    Object.freeze({ key: 'active', label: 'Active', tip: 'Projects in progress' }),
    Object.freeze({ key: 'completed', label: 'Completed', tip: 'Finished projects' }),
    Object.freeze({ key: 'archived', label: 'Archived', tip: 'Archived projects' }),
    Object.freeze({ key: 'all', label: 'All', tip: 'Every project' }),
  ]);
  const NO_CUSTOMER_KEY = '__none__';

  function statusOf(p) {
    const s = String((p && p.status) || '').trim().toLowerCase();
    return s || DEFAULT;
  }

  function statusLabel(p) {
    const s = statusOf(p);
    const chip = CHIPS.find(c => c.key === s);
    return chip ? chip.label : s.charAt(0).toUpperCase() + s.slice(1);
  }

  function isChipKey(key) {
    return CHIPS.some(c => c.key === key);
  }

  // A remembered value we no longer recognise falls back to the default rather
  // than to a filter that silently shows nothing. `extraKeys` = view-only chips
  // (AR/AP's 'owed') that the caller handles itself.
  function resolve(key, extraKeys) {
    if (isChipKey(key)) return key;
    if (Array.isArray(extraKeys) && extraKeys.includes(key)) return key;
    return DEFAULT;
  }

  function matches(p, key) {
    if (key === 'all') return true;
    if (!KNOWN.includes(key)) return false;
    return statusOf(p) === key;
  }

  function filterBy(list, key) {
    return (list || []).filter(p => matches(p, key));
  }

  function countFor(list, key) {
    return filterBy(list, key).length;
  }

  function customerGroupKey(p) {
    if (p && p.primaryCustomerId) return 'id:' + p.primaryCustomerId;
    const name = String((p && p.primaryCustomerName) || '').trim();
    return name ? 'name:' + name.toLowerCase() : NO_CUSTOMER_KEY;
  }

  // nameById(id) -> the customer doc's name (or '' when unknown).
  function customerGroupLabel(p, nameById) {
    if (customerGroupKey(p) === NO_CUSTOMER_KEY) return 'No customer';
    const own = String((p && p.primaryCustomerName) || '').trim();
    if (own) return own;
    const doc = typeof nameById === 'function' ? nameById(p.primaryCustomerId) : '';
    return String(doc || p.primaryCustomerId || 'No customer');
  }

  // The header for a WHOLE group, independent of row order (COD: the two views
  // sort rows differently, so "first row's name" could print two headers for
  // one customer). Projects that disagree on primaryCustomerName resolve to the
  // A–Z-first of their labels — same set of projects in, same header out.
  function customerGroupLabelFor(projects, nameById) {
    const labels = (projects || []).map(p => customerGroupLabel(p, nameById));
    if (!labels.length) return 'No customer';
    return labels.slice().sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }) || (a < b ? -1 : a > b ? 1 : 0))[0];
  }

  // A–Z by label, "No customer" last. Extra pinned-last keys (AR/AP's
  // multi-customer group) go just before "No customer".
  function compareGroups(a, b, lastKeys) {
    const rank = g => (g.key === NO_CUSTOMER_KEY ? 2 : (Array.isArray(lastKeys) && lastKeys.includes(g.key) ? 1 : 0));
    return (rank(a) - rank(b))
      || String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base', numeric: true });
  }

  return {
    DEFAULT,
    CHIPS,
    NO_CUSTOMER_KEY,
    statusOf,
    statusLabel,
    isChipKey,
    resolve,
    matches,
    filterBy,
    countFor,
    customerGroupKey,
    customerGroupLabel,
    customerGroupLabelFor,
    compareGroups,
  };
});
